import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../../core/services/api.service';
import { finalize } from 'rxjs/operators';
import { TranslatePipe } from '../../../../../../core/pipes/translate.pipe';

interface SettingsData {
  generalSettings: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    lang: string;
  };
  notificationSettings: { email: boolean; app: boolean };
  securityPrivacy: { showProfile: 'public' | 'registered' | 'private' };
  businessSettings: {
    companyName: string;
    commercialRegisterNumber?: string;
    yearsOfExperience: number;
    specialization: 'residential' | 'commercial' | 'both';
  };
  crmSettings: { enabled: boolean };
}

@Component({
  selector: 'app-settings',
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './settings.html',
  styleUrl: './settings.scss',
})
export class BrokerSettings implements OnInit {
  activeTab: 'general' | 'notifications' | 'security' | 'business' | 'crm' = 'general';

  settings: SettingsData = {
    generalSettings: { firstName: '', lastName: '', email: '', phoneNumber: '', lang: 'en' },
    notificationSettings: { email: false, app: false },
    securityPrivacy: { showProfile: 'public' },
    businessSettings: {
      companyName: '',
      commercialRegisterNumber: '',
      yearsOfExperience: 0,
      specialization: 'both',
    },
    crmSettings: { enabled: false },
  };

  isLoading = false;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.clearMessages();
    this.api
      .get<any>('/broker/dashboard/settings')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          // Map the API response to our settings interface
          this.settings = {
            generalSettings: {
              firstName: data.generalSettings?.firstName || '',
              lastName: data.generalSettings?.lastName || '',
              email: data.generalSettings?.email || '',
              phoneNumber: data.generalSettings?.phoneNumber || '',
              lang: data.generalSettings?.lang || 'en',
            },
            notificationSettings: {
              email: data.notificationSettings?.email || false,
              app: data.notificationSettings?.app || false,
            },
            securityPrivacy: {
              showProfile: data.securityPrivacy?.showProfile || 'public',
            },
            businessSettings: {
              companyName: data.businessSettings?.companyName || '',
              commercialRegisterNumber: data.businessSettings?.commercialRegisterNumber || '',
              yearsOfExperience: data.businessSettings?.yearsOfExperience || 0,
              specialization: data.businessSettings?.specialization || 'both',
            },
            crmSettings: {
              enabled: data.crmSettings?.enabled || false,
            },
          };
        },
        error: (err) => (this.errorMessage = err?.message || 'Failed to load settings.'),
      });
  }

  save(): void {
    this.isSaving = true;
    this.clearMessages();
    const payload = {
      generalSettings: {
        firstName: this.settings.generalSettings.firstName,
        lastName: this.settings.generalSettings.lastName,
        phoneNumber: this.settings.generalSettings.phoneNumber,
        lang: this.settings.generalSettings.lang,
      },
      notificationSettings: this.settings.notificationSettings,
      securityPrivacy: this.settings.securityPrivacy,
      businessSettings: {
        companyName: this.settings.businessSettings.companyName,
        yearsOfExperience: this.settings.businessSettings.yearsOfExperience,
        specialization: this.settings.businessSettings.specialization,
      },
      crmSettings: { crmSettings: this.settings.crmSettings.enabled },
    };
    this.api
      .put<SettingsData>('/broker/dashboard/settings', payload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.successMessage = 'Settings saved successfully.';
          setTimeout(() => (this.successMessage = ''), 3000);
        },
        error: (err) => (this.errorMessage = err?.message || 'Failed to save settings.'),
      });
  }

  setTab(tab: string): void {
    this.activeTab = tab as typeof this.activeTab;
    this.clearMessages();
  }

  private clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }
}
